import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { STATUS } from 'dpl/breeder_preferred_qualifications/utils/constants';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'dpl/common/design_system/Button';
import SmartLink from 'dpl/components/SmartLink';
import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import Icon from 'dpl/common/components/Icon';
import { featuredBreederProfilesResourceDefinition } from 'dpl/breeder_search_results/utils/queryDefinitions';
import HorizontalSlider from 'dpl/components/HorizontalSlider';

function FeaturedBreederCardItem({ iconName, description }) {
  return (
    <div className="flex items-center secondary">
      <Icon name={iconName} className="mr2" width="24px" height="24px" />
      <p className="lh-copy">{description}</p>
    </div>
  );
}

FeaturedBreederCardItem.propTypes = {
  iconName: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

function FeaturedBreederCard({ breedNamePlural, breeder }) {
  const isPreferredBreeder =
    breeder.preferred_qualification?.status === STATUS.PREFERRED;

  return (
    <div className="FeaturedBreederCard flex flex-column w-100 bg-white br4 ba b--default overflow-hidden">
      <div className="flex items-center bb b--default pv4 ph4 pv6-md ph6-md">
        <div className="dib relative mr3">
          <UserAvatar
            size="lg"
            user={{
              id: breeder.user_id,
              initials: breeder.initials,
              profile_photo_url:
                breeder.cropped_photo_url || breeder.profile_photo_url
            }}
          />
          {isPreferredBreeder && (
            <PreferredBreederBadge
              className="absolute right-0 bottom-0"
              width="28px"
              height="32px"
              hideTooltip
            />
          )}
        </div>
        <h6 className="FeaturedBreederCard__breederName font-18 md:font-20">
          {breeder.display_name} of {breeder.name}
        </h6>
      </div>
      <div className="flex flex-column flex-grow-1 pv4 ph4 font-16">
        <div className="flex flex-column mb4 gap-12">
          <FeaturedBreederCardItem
            iconName="fetch-location-house"
            description={`Located in ${breeder.facility_location}`}
          />
          <FeaturedBreederCardItem
            iconName="fetch-dog"
            description={`Raises ${breedNamePlural}`}
          />
          <FeaturedBreederCardItem
            iconName="fetch-heart-paw"
            description={breeder.availability_description}
          />
          {breeder.response_time_description && (
            <FeaturedBreederCardItem
              iconName="fetch-clock"
              description={breeder.response_time_description}
            />
          )}
        </div>
        <SmartLink
          className={classnames(
            'db mt-auto',
            BUTTON_SIZES.default,
            BUTTON_VARIANTS.outline
          )}
          target="_blank"
          to={breeder.url}
        >
          View {breeder.first_name}&apos;s profile
        </SmartLink>
      </div>
    </div>
  );
}

FeaturedBreederCard.propTypes = {
  breedNamePlural: PropTypes.string.isRequired,
  breeder: PropTypes.shape({
    availability_description: PropTypes.string,
    cropped_photo_url: PropTypes.string,
    display_name: PropTypes.string,
    facility_location: PropTypes.string,
    initials: PropTypes.string,
    first_name: PropTypes.string,
    name: PropTypes.string,
    preferred_qualification: PropTypes.shape({
      status: PropTypes.string
    }),
    profile_photo_url: PropTypes.string,
    response_time_description: PropTypes.string,
    url: PropTypes.string,
    user_id: PropTypes.number
  }).isRequired
};

export default function FeaturedBreedersModule({
  breedSlug,
  breedNamePlural,
  citySlug,
  className,
  locationTitle
}) {
  const featuredBreedersQuery = useResourceQuery({
    ...featuredBreederProfilesResourceDefinition(breedSlug, citySlug),
    enabled: Boolean(citySlug)
  });

  const featuredBreeders = featuredBreedersQuery.value.data;

  if (featuredBreeders.length === 0) {
    return null;
  }

  return (
    <div
      className={classnames(
        'FeaturedBreedersModule bg-sand br3-ns pv12 ph0 ph10-md',
        className,
        {
          'dn-ns': featuredBreeders.length < 3
        }
      )}
    >
      <h4 className="font-24 md:font-32 tc center mb6 mb10-md">
        Connect with breeders near {locationTitle}
      </h4>
      <HorizontalSlider
        containerClassName="FeaturedBreedersModuleSlider__container flex flex-nowrap gap-8 md:gap-12 ph6 ph0-sm"
        className="FeaturedBreedersModuleSlider nl6 nr6 nl0-sm nr0-sm"
        iconClassName="secondary bg-white ph2 pv2 br-100 ba b--hairline"
        iconName="fetch-chevron"
      >
        {featuredBreeders.map((breeder, idx) => (
          <div
            key={idx}
            className="flex flex-auto-xl flex-basis-0-xl ws-normal"
          >
            <FeaturedBreederCard
              breedNamePlural={breedNamePlural}
              breeder={breeder}
            />
          </div>
        ))}
      </HorizontalSlider>
    </div>
  );
}

FeaturedBreedersModule.propTypes = {
  breedSlug: PropTypes.string.isRequired,
  breedNamePlural: PropTypes.string.isRequired,
  className: PropTypes.string,
  citySlug: PropTypes.string.isRequired,
  locationTitle: PropTypes.string.isRequired
};

FeaturedBreedersModule.defaultProps = {
  className: null
};
